/**
 * Auth0 Organisation Service
 *
 * Signal-based service that fetches Auth0 organization members via
 * the Organizations API endpoint exclusively.
 *
 * Features:
 * - Single endpoint: /organizations/{orgId}/members
 * - Structured error handling via ApiHttpError (401/403 → 'Unauthorised access')
 * - Email/UPN client-side filtering support
 * - Caching with configurable TTL
 * - Element-scoped state (not providedIn: 'root')
 *
 * @packageDocumentation
 */

import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { LoggerService } from './logger.service';
import {
  Auth0OrgMember,
  Auth0OrgServiceConfig,
  Auth0GroupResponse,
  OrgMembersResponse,
  OrgMemberRaw,
  DataSource
} from '../models/auth0-org.model';
import { ApiHttpError } from '../models/api.model';
import { isScimManagedGroupId } from '../models/group.model';
import { JwtDecoderService } from './jwt-decoder.service';

/** Service tag for logging */
const SERVICE_TAG = 'Auth0OrgService';

/** Default configuration values */
const DEFAULTS = {
  timeout: 10000,
  cacheTtl: 300000, // 5 minutes
  perPage: 50 // Auth0 max per_page for organizations endpoint
} as const;

/**
 * Service for fetching Auth0 organization members.
 *
 * @remarks
 * This service is element-scoped (not providedIn: 'root') to ensure
 * each <fga-manager> web component instance has isolated state.
 * Add to component providers array when using.
 *
 * @example
 * ```typescript
 * @Component({
 *   providers: [Auth0OrganisationService]
 * })
 * export class MyComponent {
 *   private readonly orgService = inject(Auth0OrganisationService);
 *
 *   ngOnInit() {
 *     this.orgService.configure({ apiBase: 'http://localhost:3001' });
 *   }
 * }
 * ```
 */
@Injectable()
export class Auth0OrganisationService {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(LoggerService);
  private readonly jwtDecoder = inject(JwtDecoderService);

  // =========================================================================
  // Configuration
  // =========================================================================

  private config: Auth0OrgServiceConfig | null = null;

  // =========================================================================
  // Cache
  // =========================================================================

  /** In-memory cache for member data */
  private readonly cache = new Map<string, { data: Auth0OrgMember[]; timestamp: number }>();

  // =========================================================================
  // State Signals (Private Writable)
  // =========================================================================

  private readonly _members = signal<Auth0OrgMember[]>([]);
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);
  private readonly _lastSource = signal<DataSource | null>(null);
  private readonly _configured = signal<boolean>(false);
  private readonly _groupNames = signal<Record<string, string>>({});

  // =========================================================================
  // Public Readonly Signals
  // =========================================================================

  /**
   * Current list of organization members.
   * Empty array until data is loaded.
   */
  readonly members = this._members.asReadonly();

  /**
   * Whether a fetch operation is in progress.
   */
  readonly loading = this._loading.asReadonly();

  /**
   * Error message from last failed operation, or null if successful.
   */
  readonly error = this._error.asReadonly();

  /**
   * Which API method was used to fetch the current data.
   * - 'org-api': Organizations endpoint (most efficient)
   * - 'batch-query': Lucene batch query
   * - 'individual-fetch': Individual user requests
   * - null: No data loaded yet
   */
  readonly lastSource = this._lastSource.asReadonly();

  /**
   * Whether the service has been configured with API settings.
   */
  readonly configured = this._configured.asReadonly();

  /**
   * Auth0 display names of SCIM group identifiers resolved so far, keyed by
   * identifier (`grp_XXXXX`). Identifiers that failed to resolve are absent.
   */
  readonly groupNames = this._groupNames.asReadonly();

  // =========================================================================
  // Computed Signals
  // =========================================================================

  /**
   * Count of currently loaded members.
   */
  readonly memberCount = computed(() => this._members().length);

  // =========================================================================
  // Public Methods
  // =========================================================================

  /**
   * Configure the service with API settings.
   * Must be called before any fetch operations.
   *
   * @param config - Service configuration
   */
  configure(config: Auth0OrgServiceConfig): void {
    this.config = {
      ...config,
      timeout: config.timeout ?? DEFAULTS.timeout,
      cacheTtl: config.cacheTtl ?? DEFAULTS.cacheTtl
    };

    this._configured.set(true);

    this.logger.debug(SERVICE_TAG, 'Service configured', {
      apiBase: config.apiBase,
      hasAccessToken: !!config.accessToken,
      hasSubscriptionKey: !!config.subscriptionKey,
      organisationId: config.organisationId
    });
  }

  /**
   * Update the organisation ID on an already-configured service.
   * Useful when the org ID is resolved from the token after initial configuration.
   *
   * @param organisationId - Auth0 Organisation ID
   */
  setOrganisationId(organisationId: string): void {
    if (!this.config) {
      this.logger.warn(SERVICE_TAG, 'Cannot set organisationId: service not configured', undefined);
      return;
    }
    this.config.organisationId = organisationId;
    this.logger.debug(SERVICE_TAG, 'Organisation ID updated', { organisationId });
  }

  /**
   * Clear all cached data and reset state.
   * Does not clear configuration.
   */
  clearCache(): void {
    this._members.set([]);
    this._error.set(null);
    this._lastSource.set(null);
    this._groupNames.set({});
    this.cache.clear();

    this.logger.debug(SERVICE_TAG, 'Cache cleared', undefined);
  }

  /**
   * Get the display name for an Auth0 enterprise group.
   *
   * @param groupId - Auth0 group identifier beginning with `grp_`
   * @returns The non-empty display name returned by the reverse proxy
   */
  async getGroupName(groupId: string): Promise<string> {
    const url = `${this.getApiBase()}/groups/${encodeURIComponent(groupId)}`;
    const response = await firstValueFrom(
      this.http.get<Auth0GroupResponse>(url, { headers: this.buildHeaders() }).pipe(
        timeout(this.getTimeout())
      )
    );
    const name = response.name?.trim();

    if (!name) {
      throw new Error(`Auth0 group ${groupId} did not return a display name`);
    }

    return name;
  }

  async resolveGroupNames(groupIds: string[]): Promise<Record<string, string>> {
    if (!this._configured()) {
      return this._groupNames();
    }

    const cached = this._groupNames();
    const pending = [
      ...new Set(groupIds.filter(groupId => isScimManagedGroupId(groupId) && !cached[groupId])),
    ];

    if (pending.length === 0) {
      return cached;
    }

    const lookups = await Promise.all(
      pending.map(async (groupId): Promise<[string, string] | null> => {
        try {
          return [groupId, await this.getGroupName(groupId)];
        } catch (error) {
          this.logger.warn(SERVICE_TAG, 'Failed to resolve Auth0 group name', { error, groupId });
          return null;
        }
      })
    );

    const entries = lookups.filter((entry): entry is [string, string] => entry !== null);
    if (entries.length > 0) {
      this._groupNames.update(current => ({ ...current, ...Object.fromEntries(entries) }));
    }

    return this._groupNames();
  }

  // =========================================================================
  // Cache Helper Methods
  // =========================================================================

  /**
   * Generate a cache key from userIds and optional filter.
   * Sorts userIds to ensure consistent keys regardless of order.
   */
  protected getCacheKey(userIds: string[], filter?: string): string {
    const sortedIds = [...userIds].sort().join(',');
    return filter ? `${sortedIds}|filter:${filter}` : sortedIds;
  }

  /**
   * Get cached data if still valid (within TTL).
   * Returns null if no valid cache entry exists.
   */
  protected getCachedData(cacheKey: string): Auth0OrgMember[] | null {
    const entry = this.cache.get(cacheKey);
    if (!entry) {
      return null;
    }

    const ttl = this.config?.cacheTtl ?? DEFAULTS.cacheTtl;
    const now = Date.now();

    if (now - entry.timestamp > ttl) {
      // Cache expired, remove entry
      this.cache.delete(cacheKey);
      return null;
    }

    return entry.data;
  }

  /**
   * Store data in cache with timestamp.
   */
  protected setCachedData(cacheKey: string, data: Auth0OrgMember[]): void {
    this.cache.set(cacheKey, { data, timestamp: Date.now() });
  }

  // =========================================================================
  // HTTP Helper Methods
  // =========================================================================

  /**
   * Build HTTP headers for authenticated requests.
   */
  protected buildHeaders(): HttpHeaders {
    let headers = new HttpHeaders();
    const { accessToken, subscriptionKey } = this.config || {};

    if (accessToken && !this.jwtDecoder.isTokenExpired(accessToken)) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }

    if (subscriptionKey) {
      headers = headers.set('ocp-apim-subscription-key', subscriptionKey);
    }

    return headers;
  }

  /**
   * Map an HttpErrorResponse to a structured ApiHttpError.
   *
   * @param error - The HTTP error response
   * @returns ApiHttpError with appropriate code and message
   */
  protected mapHttpError(error: HttpErrorResponse): ApiHttpError {
    const status = error.status;
    let code: string;
    let message: string;

    switch (status) {
      case 401:
        code = 'UNAUTHORIZED';
        message = 'Unauthorised access';
        break;
      case 403:
        code = 'FORBIDDEN';
        message = 'Unauthorised access';
        break;
      case 429:
        code = 'RATE_LIMITED';
        message = 'Too many requests - please try again later';
        break;
      case 500:
        code = 'API_ERROR';
        message = 'Auth0 server error - please try again later';
        break;
      case 502:
      case 503:
      case 504:
        code = 'SERVICE_UNAVAILABLE';
        message = `Auth0 service unavailable (${status})`;
        break;
      default:
        code = 'API_ERROR';
        message = `HTTP error ${status}: ${error.statusText}`;
    }

    return { status, code, message, timestamp: Date.now() };
  }

  /**
   * Get the configured timeout value.
   */
  protected getTimeout(): number {
    return this.config?.timeout ?? DEFAULTS.timeout;
  }

  /**
   * Get the API base URL.
   * @throws Error if service not configured
   */
  protected getApiBase(): string {
    if (!this.config?.apiBase) {
      throw new Error('Auth0OrganisationService not configured. Call configure() first.');
    }
    return this.config.apiBase;
  }



  // =========================================================================
  // Organizations Endpoint (Primary Method)
  // =========================================================================

  /**
   * Load all members of an organization via the Organizations API.
   * This is the most efficient method when the endpoint is available.
   *
   * @param orgId - Auth0 Organization ID
   * @param options - Optional filter settings
   * @returns Promise resolving to array of org members
   * @throws Error on API failure (caller should handle fallback)
   */
  async loadMembersByOrgId(
    orgId: string,
    options?: { filter?: string }
  ): Promise<Auth0OrgMember[]> {
    this.getApiBase(); // Validate configuration
    
    this._loading.set(true);
    this._error.set(null);

    try {
      let allMembers = await this.fetchViaOrganizationsEndpoint(orgId);

      // Apply client-side filter if provided (org endpoint doesn't support Lucene)
      if (options?.filter) {
        allMembers = this.applyClientSideFilter(allMembers, options.filter);
      }

      this._members.set(allMembers);
      this._lastSource.set('org-api');
      this._loading.set(false);

      return allMembers;
    } catch (error) {
      this._loading.set(false);

      if (error instanceof HttpErrorResponse) {
        const apiError = this.mapHttpError(error);
        this._error.set(apiError.message);

        this.logger.warn(SERVICE_TAG, 'Organizations endpoint failed', {
          orgId,
          status: error.status,
          code: apiError.code,
          message: apiError.message
        });

        throw apiError;
      }

      const errorMsg = `Organizations endpoint failed: ${String(error)}`;
      this._error.set(errorMsg);

      this.logger.warn(SERVICE_TAG, 'Organizations endpoint failed', {
        orgId,
        error
      });

      throw error;
    }
  }

  /**
   * Fetch members from the Organizations API with checkpoint pagination.
   *
   * Uses Auth0 checkpoint pagination (`from`/`take`) instead of offset pagination
   * to reliably handle >1000 members.
   *
   * @param orgId - Auth0 Organization ID
   * @returns Promise resolving to all members across all pages
   */
  protected async fetchViaOrganizationsEndpoint(orgId: string): Promise<Auth0OrgMember[]> {
    const headers = this.buildHeaders();
    const take = DEFAULTS.perPage;
    const allMembers: Auth0OrgMember[] = [];
    let from: string | undefined;

    while (true) {
      let url = `${this.getApiBase()}/organizations/${orgId}/members?take=${take}`;
      if (from) {
        url += `&from=${encodeURIComponent(from)}`;
      }

      // Auth0 Management API v2 may return either:
      //   - A flat array: OrgMemberRaw[]  (actual Auth0 behaviour)
      //   - A checkpoint response: { members: OrgMemberRaw[], next?: string }
      const raw = await firstValueFrom(
        this.http.get<OrgMembersResponse | OrgMemberRaw[]>(url, { headers }).pipe(
          timeout(this.getTimeout())
        )
      );

      const isArray = Array.isArray(raw);
      const membersList: OrgMemberRaw[] = isArray ? raw : raw.members;

      const fetchedAt = Date.now();
      const membersWithMeta: Auth0OrgMember[] = membersList.map(m => ({
        user_id: m.user_id,
        email: m.email,
        name: m.name,
        fetchedAt,
        source: 'org-api' as const
      }));

      allMembers.push(...membersWithMeta);

      // Determine if there are more pages
      if (isArray) {
        // Flat array: stop when fewer results than requested
        if (membersList.length < take) break;
        // No cursor available from flat array — stop after first page
        break;
      } else {
        // Checkpoint response: use 'next' cursor
        const nextCursor = (raw as { next?: string }).next;
        if (!nextCursor) break;
        from = nextCursor;
      }
    }

    return allMembers;
  }

  // =========================================================================
  // Client-Side Filtering
  // =========================================================================

  /**
   * Filter members by email or name (case-insensitive partial match).
   * Used for endpoints that don't support Lucene queries (Organizations, Individual).
   *
   * @param members - Array of members to filter
   * @param filter - Search string to match against email or name
   * @returns Filtered array of members
   */
  protected applyClientSideFilter(
    members: Auth0OrgMember[],
    filter: string
  ): Auth0OrgMember[] {
    const lowerFilter = filter.toLowerCase();
    return members.filter(
      m =>
        m.email?.toLowerCase().includes(lowerFilter) ||
        m.name?.toLowerCase().includes(lowerFilter)
    );
  }

  // =========================================================================
  // Main Entry Point (Organization Endpoint Only)
  // =========================================================================

  /**
   * Get organisation members via the Organizations API endpoint.
   *
   * Requires `organisationId` to be configured. Does not fall back to
   * Lucene batch queries or individual fetches — errors are surfaced
   * as structured `ApiHttpError` instances.
   *
   * @param userIds - Array of user IDs to fetch details for
   * @param options - Optional filter settings
   * @returns Promise resolving to array of enriched members
   * @throws ApiHttpError on HTTP failure (401/403 → 'Unauthorised access')
   * @throws Error if organisationId is not configured or userIds is empty
   */
  async getOrganisationMembers(
    userIds: string[],
    options?: { filter?: string }
  ): Promise<Auth0OrgMember[]> {
    if (!userIds || userIds.length === 0) {
      throw new Error('userIds parameter is required and must not be empty');
    }

    const orgId = this.config?.organisationId;
    if (!orgId) {
      throw new Error('organisationId must be configured to fetch organisation members');
    }

    // Check cache first
    const cacheKey = this.getCacheKey(userIds, options?.filter);
    const cachedData = this.getCachedData(cacheKey);

    if (cachedData) {
      this.logger.debug(SERVICE_TAG, 'Returning cached data', {
        cacheKey,
        memberCount: cachedData.length
      });
      this._members.set(cachedData);
      return cachedData;
    }

    this.logger.info(SERVICE_TAG, 'Organization endpoint: fetching members', {
      orgId,
      userCount: userIds.length,
    });

    const result = await this.loadMembersByOrgId(orgId, options);
    this.setCachedData(cacheKey, result);
    return result;
  }

  // =========================================================================
  // Remove Members
  // =========================================================================

  /**
   * Remove members from the organisation.
   *
   * Calls `DELETE /organizations/{orgId}/members` with a body containing
   * the user IDs to remove.
   *
   * @param memberIds - Array of Auth0 user IDs (without "user:" prefix)
   * @throws ApiHttpError on failure
   */
  async removeMembers(memberIds: string[]): Promise<void> {
    const apiBase = this.getApiBase();
    const orgId = this.config?.organisationId;

    if (!orgId) {
      throw new Error('Organisation ID not configured. Call configure() or setOrganisationId() first.');
    }

    const headers = this.buildHeaders();
    const url = `${apiBase}/organizations/${orgId}/members`;

    await firstValueFrom(
      this.http.delete<void>(url, {
        headers,
        body: { members: memberIds },
      }).pipe(timeout(this.getTimeout()))
    );

    this.cache.clear();

    this.logger.info(SERVICE_TAG, 'Members removed successfully', {
      orgId,
      removedCount: memberIds.length,
    });
  }
}
